import { PersonChip } from "@/components/PersonChip";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import MultiSelect from "@/components/ui/multi-select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { UserPlus, Trash2, Shield, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";

const INTERNAL_ROLES = ["admin", "manager", "designer", "viewer"];

/**
 * ProjectMembersTab — assign internal members and external collaborators to a project.
 * Internal members are picked from existing workspace_members. External users are added by email
 * (they become workspace_members with role=external and project_members with is_external=true).
 */
export default function ProjectMembersTab({ projectId, workspaceId, myPerms = [] }) {
  const canManage = myPerms.includes("project.member.manage");
  const [members, setMembers] = useState([]);
  const [wsMembers, setWsMembers] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [busy, setBusy] = useState(true);

  const load = async () => {
    setBusy(true);
    try {
      const [m, ws, cr] = await Promise.all([
        api.get(`/projects/${projectId}/members`),
        api.get(`/workspaces/${workspaceId}/members`),
        api.get(`/workspaces/${workspaceId}/custom-roles`).catch(() => ({ data: [] })),
      ]);
      setMembers(m.data || []);
      setWsMembers(ws.data || []);
      setCustomRoles(cr.data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load members"));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [projectId]);

  const removeMember = async (uid) => {
    if (!confirm("Remove this person from the project?")) return;
    try {
      await api.delete(`/projects/${projectId}/members/${uid}`);
      toast.success("Removed");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't remove")); }
  };

  const setMemberRole = async (m, patch) => {
    try {
      await api.patch(`/projects/${projectId}/members/${m.user_id}`, patch);
      toast.success("Project role updated");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  return (
    <div className="space-y-4" data-testid="project-members-tab">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow">Project access</div>
          <p className="text-xs text-muted-foreground mt-1">
            Only people listed below can see this project, its meetings, action items, milestones, and RAID log.
            Workspace owners / admins always have full access.
          </p>
        </div>
        {canManage && (
          <AddMemberDialog
            projectId={projectId}
            workspaceId={workspaceId}
            wsMembers={wsMembers}
            existingMemberUserIds={members.map((m) => m.user_id)}
            customRoles={customRoles}
            onSaved={load}
          />
        )}
      </div>

      {busy && members.length === 0 ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : members.length === 0 ? (
        <div className="border border-border p-6 text-center text-sm text-muted-foreground" data-testid="project-members-empty">
          No one assigned to this project yet.
        </div>
      ) : (
        <div className="border border-border" data-testid="project-members-table">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border label-eyebrow text-[10px] bg-muted/40">
            <div className="col-span-4">Person</div>
            <div className="col-span-2">Role</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-2">Added</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {members.map((m) => (
            <div key={m.user_id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border items-center text-sm" data-testid={`project-member-row-${m.user_id}`}>
              <div className="col-span-4 min-w-0">
                <div className="font-medium truncate"><PersonChip userId={m.user_id} name={m.user?.name || m.user?.email} /></div>
                <div className="text-xs text-muted-foreground truncate">{m.user?.email}</div>
              </div>
              <div className="col-span-2">
                {canManage ? (
                  <Select value={m.role} onValueChange={(v) => setMemberRole(m, { role: v })}>
                    <SelectTrigger className="h-7 text-xs" data-testid={`pm-role-${m.user_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{["admin", "manager", "designer", "viewer", "external"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <span className="chip text-[10px]">{m.role}</span>
                )}
                {canManage ? (
                  <Select value={m.custom_role_id || "_none"} onValueChange={(v) => setMemberRole(m, { custom_role_id: v === "_none" ? null : v })}>
                    <SelectTrigger className="h-6 text-[10px] mt-1" data-testid={`pm-customrole-${m.user_id}`}><SelectValue placeholder="No overlay" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">No overlay</SelectItem>
                      {customRoles.length === 0 ? (
                        <div className="px-2 py-1.5 text-[10px] text-muted-foreground">No custom roles defined. Create one under Roles.</div>
                      ) : customRoles.filter((cr) => cr.is_active !== false).map((cr) => <SelectItem key={cr.id} value={cr.id}>{cr.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : m.custom_role ? (
                  <div className="text-[10px] mt-0.5">+ {m.custom_role.name}</div>
                ) : null}
              </div>
              <div className="col-span-2">
                {m.is_external ? (
                  <span className="chip chip-client inline-flex items-center gap-1 text-[10px]" data-testid={`pm-type-external-${m.user_id}`}>
                    <Shield className="w-3 h-3" /> external
                  </span>
                ) : (
                  <span className="chip chip-approved inline-flex items-center gap-1 text-[10px]"><ShieldCheck className="w-3 h-3" /> internal</span>
                )}
              </div>
              <div className="col-span-2 text-xs text-muted-foreground">{fmtRelative(m.created_at)}</div>
              <div className="col-span-2 text-right">
                {canManage && (
                  <Button size="sm" variant="ghost" onClick={() => removeMember(m.user_id)} className="rounded-none h-7 px-2 text-xs" data-testid={`pm-remove-${m.user_id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddMemberDialog({ projectId, workspaceId, wsMembers, existingMemberUserIds, customRoles, onSaved }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("internal");
  const [pickUids, setPickUids] = useState([]);
  const [pickContactId, setPickContactId] = useState("");
  const [extEmail, setExtEmail] = useState("");
  const [extName, setExtName] = useState("");
  const [role, setRole] = useState("designer");
  const [customRoleId, setCustomRoleId] = useState("");
  const [busy, setBusy] = useState(false);
  const [clientContacts, setClientContacts] = useState([]);

  useEffect(() => {
    if (!open) return;
    // Resolve client_id of this project, then load its contacts
    api.get(`/projects/${projectId}`).then(async ({ data }) => {
      if (data?.client_id) {
        const { data: contacts } = await api.get(`/clients/${data.client_id}/contacts`);
        setClientContacts(contacts || []);
      }
    }).catch(() => setClientContacts([]));
  }, [open, projectId]);

  // Feb 2026 R293m · Only ACTIVE workspace members, exclude any user
  // already on this project, sort alphabetically. The old picker also
  // included deactivated/invited members and sorted by insertion
  // order, which made the "Add internal team member" dropdown messy.
  const eligibleInternal = (wsMembers || [])
    .filter((m) => !existingMemberUserIds.includes(m.user_id)
                      && m.role !== "external"
                      && (m.status || "active") === "active")
    .slice()
    .sort((a, b) => {
      const an = (a.name || a.email || a.user?.name || "").toLowerCase();
      const bn = (b.name || b.email || b.user?.name || "").toLowerCase();
      return an.localeCompare(bn);
    });
  const eligibleContacts = clientContacts.filter((c) => !existingMemberUserIds.includes(c.user_id));

  const reset = () => { setPickUids([]); setPickContactId(""); setExtEmail(""); setExtName(""); setRole("designer"); setCustomRoleId(""); setTab("internal"); };

  const submit = async () => {
    setBusy(true);
    try {
      if (tab === "contact") {
        if (!pickContactId) { toast.error("Pick a CRM contact"); return; }
        await api.post(`/projects/${projectId}/assign-contact`, { contact_id: pickContactId });
        if (customRoleId) {
          const contact = clientContacts.find((c) => c.id === pickContactId);
          await api.patch(`/projects/${projectId}/members/${contact.user_id}`, { custom_role_id: customRoleId });
        }
        toast.success("Contact assigned to project");
      } else if (tab === "internal") {
        // Feb 2026 R293m · Multi-member add. Fire one POST per
        // selected user so each gets the same role / custom_role.
        if (!pickUids.length) { toast.error("Pick at least one workspace member"); return; }
        const results = await Promise.allSettled(pickUids.map((uid) =>
          api.post(`/projects/${projectId}/members`, {
            user_id: uid,
            role,
            is_external: false,
            custom_role_id: customRoleId || null,
          })
        ));
        const ok = results.filter((r) => r.status === "fulfilled").length;
        const failed = results.length - ok;
        if (ok) toast.success(`Added ${ok} member${ok === 1 ? "" : "s"} to project`);
        if (failed) {
          const first = results.find((r) => r.status === "rejected");
          toast.error(apiErrorMessage(first?.reason, `${failed} assignment${failed === 1 ? "" : "s"} failed`));
        }
      } else {
        // external by email
        const body = { role: "external", is_external: true,
                        custom_role_id: customRoleId || null };
        if (!extEmail) { toast.error("Email required"); return; }
        body.email = extEmail;
        body.name = extName || null;
        await api.post(`/projects/${projectId}/members`, body);
        toast.success("Added to project");
      }
      setOpen(false); reset();
      onSaved && onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't add member"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-none" data-testid="project-members-add-btn">
          <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Assign people
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl" data-testid="project-members-add-dialog">
        <DialogHeader>
          <DialogTitle>Assign to this project</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant={tab === "internal" ? "default" : "outline"} onClick={() => setTab("internal")} className="rounded-none" data-testid="add-tab-internal">Internal team</Button>
            <Button size="sm" variant={tab === "contact" ? "default" : "outline"} onClick={() => setTab("contact")} className="rounded-none" data-testid="add-tab-contact">From CRM contacts</Button>
            <Button size="sm" variant={tab === "external" ? "default" : "outline"} onClick={() => setTab("external")} className="rounded-none" data-testid="add-tab-external">External by email</Button>
          </div>

          {tab === "contact" && (
            <div>
              <div className="label-eyebrow mb-1">Client contact</div>
              {eligibleContacts.length === 0 ? (
                <div className="text-xs border border-dashed border-border p-3" data-testid="add-contact-empty">
                  No more client contacts to assign. Add stakeholders under the client&apos;s Contacts tab first.
                </div>
              ) : (
                <Select value={pickContactId} onValueChange={setPickContactId}>
                  <SelectTrigger className="h-9 text-sm" data-testid="add-contact-pick"><SelectValue placeholder="Pick a CRM contact…" /></SelectTrigger>
                  <SelectContent>
                    {eligibleContacts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}{c.title ? ` · ${c.title}` : ""}{c.is_primary ? " · primary" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <div className="border border-blue-500/30 bg-blue-50/40 dark:bg-blue-950/20 p-2.5 text-xs mt-3">
                CRM contacts already exist in this client&apos;s roster. They&apos;ll be assigned as external members and inherit their CRM portal role unless you override below.
              </div>
            </div>
          )}

          {tab === "internal" && (
            <div>
              <div className="label-eyebrow mb-1">Pick workspace members</div>
              {eligibleInternal.length === 0 ? (
                <div className="px-3 py-2.5 text-xs text-muted-foreground border border-dashed border-border">
                  All active workspace members are already assigned.
                </div>
              ) : (
                <MultiSelect
                  // Feb 2026 R293m · Searchable, sorted, multi-select
                  // picker. Each option label becomes "Full Name
                  // (Designation)" so admins can tell apart two team-
                  // mates with the same first name. Width stretches to
                  // the dialog so long designations stay readable.
                  options={eligibleInternal.map((m) => {
                    const name = m.name || m.user?.name || m.email || m.user?.email || "User";
                    const desg = m.designation || m.user?.designation || "";
                    const label = desg ? `${name} (${desg})` : name;
                    return { value: m.user_id, label };
                  })}
                  value={pickUids}
                  onChange={setPickUids}
                  placeholder="Choose team members…"
                  allLabel="Add internal team members"
                  width="w-full"
                  testid="add-member-pick"
                />
              )}
              <div className="label-eyebrow mt-3 mb-1">Project-level role</div>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="h-9 text-sm" data-testid="add-member-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INTERNAL_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              {pickUids.length > 1 && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  All {pickUids.length} people will be added with the role <strong>{role}</strong>.
                </p>
              )}
            </div>
          )}

          {tab === "external" && (
            <div className="space-y-3">
              <div>
                <div className="label-eyebrow mb-1">Email <span className="text-amber-500">*</span></div>
                <Input type="email" value={extEmail} onChange={(e) => setExtEmail(e.target.value)} placeholder="client@acme.com" data-testid="add-ext-email" />
              </div>
              <div>
                <div className="label-eyebrow mb-1">Name (optional)</div>
                <Input value={extName} onChange={(e) => setExtName(e.target.value)} placeholder="Jane Smith" data-testid="add-ext-name" />
              </div>
              <div className="border border-amber-500/30 bg-amber-50/40 dark:bg-amber-950/20 p-2.5 text-xs">
                <strong>External user.</strong> They&apos;ll get the &quot;external&quot; default role — see only this project&apos;s milestones, RAID log items marked &quot;visible to client&quot;, external meetings, and review items they&apos;re explicitly invited to.
              </div>
            </div>
          )}

          {customRoles.length > 0 && (
            <div>
              <div className="label-eyebrow mb-1">Custom role overlay (optional)</div>
              <Select value={customRoleId || "_none"} onValueChange={(v) => setCustomRoleId(v === "_none" ? "" : v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="add-member-custom-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">None</SelectItem>
                  {customRoles.filter((r) => r.is_active !== false).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">A custom role&apos;s permissions are added on top of the default role&apos;s permissions.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)} data-testid="add-member-cancel">Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="add-member-submit">
            {busy ? "Saving…" : "Assign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
